import type { FC } from 'react';
import { OptionalClassWrapper } from './OptionalClass.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface OptionalClassProps { }

const OptionalClass: FC<OptionalClassProps> = () => (
   <OptionalClassWrapper>
      <header className="entry-header ">
         <h1 className="entry-title" itemProp="headline">Optional Class</h1>
      </header>
      <br/>
      <div className="entry-content clear" itemProp="text" style={{ height: 'auto !important' }}>
         <p className="text-align-justify">Optional Class is a new feature introduced in Java 8. This Optional class is present in java.util package. It helps one in writing a neat code without much use of null checks. By using Optional, we can determine the alternate values to return or the alternate code to run. In other words, to avoid abnormal termination, we use the Optional class. Optional is basically a container object which may or may not contain a non-null value. It is a public final class that deals with NullPointerException in Java application.</p>
         <br/>
         <p className="text-align-justify"><strong>Declaration :</strong></p>
         <p className="text-align-justify">{'public final class Optional<T> extends Object'}</p>
         <br/>
         <p className="text-align-justify"><strong>Some utility methods available in optional class are :</strong></p>
         <ul className="text-align-justify">
            <li><strong>isPresent()</strong>
               <ul>
                  <li>If a value is present, isPresent() will return true.</li>
               </ul>
            </li>
            <li><strong>get()</strong>
               <ul>
                  <li>It will return the value if isPresent() returns true.</li>
               </ul>
            </li>
            <li><strong>orElse()</strong>
               <ul>
                  <li>It returns a default value if value not present.</li>
               </ul>
            </li>
            <li><strong>empty()</strong>
               <ul>
                  <li>It returns an empty Optional instance.</li>
               </ul>
            </li>
            <li><strong>filter()</strong>
               <ul>
                  <li>If a value is present and the value matches a given predicate, it returns an Optional describing the value, otherwise returns an empty Optional.</li>
               </ul>
            </li>
            <li><strong>equals()</strong>
               <ul>
                  <li>Indicates whether some other object is “equal to” this Optional.</li>
               </ul>
            </li>
            <li><strong>ofNullable()</strong>
               <ul>
                  <li>It returns an Optional describing the specified value, if non-null, otherwise returns an empty Optional.</li>
               </ul>
            </li>
            <li><strong>orElseGet()</strong>
               <ul>
                  <li>It returns the value if present, otherwise invokes others, and returns the result of that invocation.</li>
               </ul>
            </li>
            <li><strong>orElseThrow()</strong>
               <ul>
                  <li>It returns the contained value, if present, otherwise throws an exception to be created by the provided supplier.</li>
               </ul>
            </li>
         </ul>
         <br/>
         <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
            {
               'import java.util.Optional;\n'+
               '\n'+
               'public class OptionalExample {\n'+
               '  public static void main(String args[]) {\n'+
               '    String[] str = new String[10];\n'+
               '    str[5] = "OptionalClass";\n'+
               '\n'+
               '    //It returns an empty instance of Optional class\n'+
               '    Optional <String> empty = Optional.empty();\n'+
               '\n'+
               '    //It returns a non-empty Optional\n'+
               '    Optional <String> value = Optional.of(str[5]);\n'+
               '\n'+
               '    //If value is present, it returns Optional\n'+
               '    //If value is not present, it returns an empty Optional\n'+
               '    System.out.println(value.filter(\n'+
               '      (s) -> s.equals("ABC")));\n'+
               '\n'+
               '    //If value is present, it returns value of an Optional\n'+
               '    //If value is not present, it throws an NoSuchElementException\n'+
               '    System.out.println("Getting value: " + value.get());\n'+
               '\n'+
               '    //It returns hashCode of the value\n'+
               '    System.out.println("Getting hashCode: " + value.hashCode());\n'+
               '\n'+
               '    //It returns true if value is present, otherwise false\n'+
               '    System.out.println("Is value present: " + value.isPresent());\n'+
               '\n'+
               '    //If value is present, it returns non-empty Optional\n'+
               '    //If value is not present, it returns an empty Optional\n'+
               '    System.out.println(Optional.ofNullable(str[5]));\n'+
               '\n'+
               '    //It returns value if available, otherwise returns specified value\n'+
               '    System.out.println(+value.orElse("Value is not present"));\n'+
               '\n'+
               '  }\n'+
               '}'
            }
         </SyntaxHighlighter>
      </div>
   </OptionalClassWrapper>
);

export default OptionalClass;
